import { useEffect, useId, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { usePromoteAgentSession } from "../../api/queries";
import { ApiError } from "../../api/errors";
import type { PromoteSessionResult } from "../../api/client";
import type { AgentSession, CorpusBinding } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import "../../components/Form.css";
import "./develop.css";

interface ProposeDevelopDialogProps {
  session: AgentSession | null;
  projectId: string;
  projectName: string;
  /** The project's corpus binding, when one exists — used to default the base
   * branch to a permitted ref and to name the bound repository in the
   * refusal copy. `undefined` while still loading OR when there is none. */
  binding: CorpusBinding | undefined;
  /** True once `useCorpusBinding` has confirmed (a 404) that this project has
   * no binding at all — distinct from "still loading," so the dialog can
   * warn BEFORE the operator even tries (A-10: don't wait for a guaranteed
   * server refusal to say what the client already knows). */
  bindingConfirmedMissing: boolean;
  onClose: () => void;
}

/**
 * The exact client-facing strings the server uses for the two ways a
 * DEVELOP session's promote is refused by the R1==R2 corpus binding-match
 * (ADR-0052 M6 slice 2) — matched here so each gets its own actionable
 * message instead of a raw error dump (S-15/Q-7):
 *
 *   - No binding at all: `agentsession.Service.resolveCorpusBinding` refuses
 *     BEFORE the request ever reaches the daemon (400, surfaces as
 *     `mutation.error`).
 *   - A binding exists but the session's own worktree remote doesn't match
 *     it (or the base branch isn't a permitted ref, or the remote couldn't
 *     be resolved at all): the DAEMON refuses fail-closed and every one of
 *     those reasons collapses to this SAME fixed, body-free string
 *     (`agent/bonnie/devlayer/session.go`'s `verifyCorpusBinding` call site)
 *     — deliberately so the response can never leak which specific reason
 *     it was (S-13). It comes back as `result.error` with `result.pushed
 *     === false` (a 200-level "the call worked, the propose didn't").
 */
const NO_BINDING_MARKER = "no corpus binding";
const BINDING_MISMATCH_MESSAGE = "repository does not match the project's corpus binding";

function isNoBindingError(error: unknown): boolean {
  return error instanceof ApiError && error.message.includes(NO_BINDING_MARKER);
}

/** Strips a `refs/heads/` prefix so a binding's permitted ref reads as a
 * plain branch name in the base-branch field — mirrors
 * `baseRefPermitted`'s (agent/bonnie/devlayer/bindingmatch.go) own three
 * interchangeable shapes, just picking the friendliest one to display. */
function refToBranchName(ref: string): string {
  return ref.startsWith("refs/heads/") ? ref.slice("refs/heads/".length) : ref;
}

/**
 * Proposes a DEVELOP session's drafted corpus: pushes the worktree branch
 * and opens a pull request (`PromoteSession` with `openPr: true` — Q3=B,
 * propose is always human-triggered and always opens a PR, never a silent
 * push). Unlike the general-purpose `PromoteSessionDialog`, the base branch
 * is ALWAYS collected and ALWAYS sent: a develop session's promote is
 * corpus-bound server-side purely because it has a project (never
 * conditional on `openPr`), and `proto.BonniePromoteRequest.Validate`
 * refuses a corpus-bound promote with no base branch outright — see
 * `PromoteSessionInput.baseBranch`'s doc comment.
 */
export function ProposeDevelopDialog({
  session,
  projectId,
  projectName,
  binding,
  bindingConfirmedMissing,
  onClose,
}: ProposeDevelopDialogProps) {
  const baseBranchId = useId();
  const prTitleId = useId();
  const prBodyId = useId();
  const mutation = usePromoteAgentSession();
  const [baseBranch, setBaseBranch] = useState("");
  const [prTitle, setPrTitle] = useState("");
  const [prBody, setPrBody] = useState("");
  const [result, setResult] = useState<PromoteSessionResult | null>(null);

  useEffect(() => {
    if (session) {
      setBaseBranch(binding && binding.allowedRefs.length > 0 ? refToBranchName(binding.allowedRefs[0]) : "");
      setPrTitle(`Propose corpus updates for ${projectName}`);
      setPrBody("");
      setResult(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, binding]);

  function handleClose() {
    setResult(null);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) return;
    mutation.mutate(
      {
        id: session.id,
        input: {
          baseBranch: baseBranch.trim(),
          openPR: true,
          prTitle: prTitle.trim(),
          prBody: prBody.trim() || undefined,
        },
      },
      {
        onSuccess: (res) => setResult(res),
      },
    );
  }

  const corpusHref = `/projects/${projectId}/corpus`;
  const bindCorpusAction = (
    <Link to={corpusHref} className="button button--secondary">
      Go to Corpus page
    </Link>
  );

  return (
    <Modal open={session !== null} title="Propose your draft" onClose={handleClose} initialFocusId={baseBranchId}>
      {session && !result && (
        <form onSubmit={handleSubmit} noValidate>
          <p>
            Push <strong>{session.label}</strong>
            {session.branch ? (
              <>
                {" "}
                (<code>{session.branch}</code>)
              </>
            ) : null}{" "}
            to <strong>{projectName}</strong>'s bound repository and open a pull request for review.
            This runs on your own machine with your own git/gh credentials.
          </p>

          {bindingConfirmedMissing && (
            <Banner
              tone="warning"
              title="This project has no corpus binding yet"
              className="develop-dialog-banner"
              action={bindCorpusAction}
            >
              Bind a repository before proposing — Comebye needs to know which repository this
              project's corpus syncs from so it can verify the draft lands in the right place.
            </Banner>
          )}

          {binding && (
            <p className="form-note">
              Bound to <code>{binding.repoRef}</code>. The base branch below must be one of:{" "}
              {binding.allowedRefs.map(refToBranchName).join(", ")}.
            </p>
          )}

          <label className="form-field" htmlFor={baseBranchId}>
            <span>Base branch</span>
            <input
              id={baseBranchId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={baseBranch}
              onChange={(e) => setBaseBranch(e.target.value)}
              required
              maxLength={200}
              placeholder="e.g. main"
            />
            <span className="form-field__hint">
              The branch this project's corpus is synced from — required to verify the draft
              against the corpus binding.
            </span>
          </label>

          <label className="form-field" htmlFor={prTitleId}>
            <span>Pull request title</span>
            <input
              id={prTitleId}
              type="text"
              autoComplete="off"
              value={prTitle}
              onChange={(e) => setPrTitle(e.target.value)}
              required
              maxLength={300}
            />
          </label>

          <label className="form-field" htmlFor={prBodyId}>
            <span>Pull request description (optional)</span>
            <textarea id={prBodyId} rows={4} value={prBody} onChange={(e) => setPrBody(e.target.value)} maxLength={8000} />
          </label>

          {mutation.isError &&
            mutation.error &&
            (() => {
              const err = mutation.error;
              return isNoBindingError(err) ? (
                <Banner tone="warning" title="Can't propose yet" className="develop-dialog-banner" action={bindCorpusAction}>
                  This project has no corpus binding — bind a repository before proposing.
                </Banner>
              ) : (
                <FormError>{err.message || "Couldn't propose this draft."}</FormError>
              );
            })()}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={mutation.isPending || !baseBranch.trim() || !prTitle.trim()}>
              {mutation.isPending ? "Proposing…" : "Propose"}
            </Button>
          </div>
        </form>
      )}

      {session && result && (
        <div>
          {result.pushed ? (
            <Banner tone="info" title="Proposed" className="develop-dialog-banner">
              "{session.label}"{result.branch ? <> (<code>{result.branch}</code>)</> : null} was pushed
              to {projectName}'s repository.
            </Banner>
          ) : result.error === BINDING_MISMATCH_MESSAGE ? (
            <Banner
              tone="warning"
              title="This session's repository doesn't match the corpus binding"
              className="develop-dialog-banner"
              action={bindCorpusAction}
            >
              {binding ? (
                <>
                  {projectName}'s corpus is bound to <code>{binding.repoRef}</code>, but this session's
                  worktree remote points somewhere else (or its base branch isn't a permitted ref). Push
                  your worktree to the bound repository — or update the binding — then propose again.
                </>
              ) : (
                <>
                  This session's repository doesn't match {projectName}'s corpus binding. Check the
                  binding on the Corpus page, then propose again.
                </>
              )}
            </Banner>
          ) : (
            <Banner tone="danger" title="Propose failed" className="develop-dialog-banner">
              {result.error || "The push didn't go through."}
            </Banner>
          )}

          {result.pushed && result.prUrl && (
            <p>
              <a href={result.prUrl} target="_blank" rel="noreferrer noopener">
                View pull request
              </a>
            </p>
          )}

          {result.pushed && !result.prUrl && (
            <Banner tone="warning" title="Pull request wasn't opened" className="develop-dialog-banner">
              {result.error || "The branch pushed, but opening a pull request failed."}
            </Banner>
          )}

          <div className="form-actions">
            <Button type="button" variant="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
